import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../utils/api';

const DOMAIN_LABELS = {
  pranic_healing: 'Pranic Healing',
  reiki:          'Reiki',
  ayurveda:       'Ayurveda',
  yoga:           'Yoga',
  physiotherapy:  'Physiotherapy',
  naturopathy:    'Naturopathy',
  generic:        'Energy Healing'
};

const COUNTRY_CODES = [
  { group: 'South Asia',  label: 'India (+91)',         value: '+91'  },
  { group: 'South Asia',  label: 'Sri Lanka (+94)',      value: '+94'  },
  { group: 'South Asia',  label: 'Bangladesh (+880)',    value: '+880' },
  { group: 'South Asia',  label: 'Nepal (+977)',         value: '+977' },
  { group: 'SE Asia',     label: 'Singapore (+65)',      value: '+65'  },
  { group: 'SE Asia',     label: 'Malaysia (+60)',       value: '+60'  },
  { group: 'SE Asia',     label: 'Thailand (+66)',       value: '+66'  },
  { group: 'SE Asia',     label: 'Indonesia (+62)',      value: '+62'  },
  { group: 'SE Asia',     label: 'Philippines (+63)',    value: '+63'  },
  { group: 'SE Asia',     label: 'Vietnam (+84)',        value: '+84'  },
  { group: 'Middle East', label: 'UAE (+971)',           value: '+971' },
  { group: 'Middle East', label: 'Saudi Arabia (+966)',  value: '+966' },
  { group: 'Middle East', label: 'Qatar (+974)',         value: '+974' },
  { group: 'Middle East', label: 'Bahrain (+973)',       value: '+973' },
  { group: 'Middle East', label: 'Oman (+968)',          value: '+968' },
  { group: 'Middle East', label: 'Kuwait (+965)',        value: '+965' },
  { group: 'Diaspora',    label: 'USA / Canada (+1)',    value: '+1'   },
  { group: 'Diaspora',    label: 'UK (+44)',             value: '+44'  },
  { group: 'Diaspora',    label: 'Australia (+61)',      value: '+61'  },
  { group: 'Diaspora',    label: 'New Zealand (+64)',    value: '+64'  },
];
const GROUPS = [...new Set(COUNTRY_CODES.map(c => c.group))];

// ── Inline SVG icons (no emoji anywhere) ─────────────────────────────────────
function IcPin() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9094AE"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
      <circle cx="12" cy="10" r="3"/>
    </svg>
  );
}

function IcLock({ size = 11, color = '#2ABFAB' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <rect x="3" y="11" width="18" height="11" rx="2"/>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
  );
}

function IcCheck({ size = 11, color = '#2ABFAB' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
      strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  );
}

function IcShield({ size = 11, color = '#2ABFAB' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  );
}

function IcLeaf({ size = 13, color = '#1A9E8C' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <path d="M2 22L12 2M22 2s-2 14-12 18"/>
    </svg>
  );
}

function IcChevron() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9094AE"
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9"/>
    </svg>
  );
}

// ── CSS injected once ─────────────────────────────────────────────────────────
const PAGE_STYLES = (
  <style>{`
    .sb-input {
      font-family: 'Inter', -apple-system, sans-serif !important;
      transition: border-color 0.2s, box-shadow 0.2s;
    }
    .sb-input:focus {
      outline: none;
      border-color: #2ABFAB !important;
      box-shadow: 0 0 0 3px rgba(42,191,171,0.12) !important;
    }
    .sb-input::placeholder { color: #B0B3C8; }
    .sb-select { appearance: none; -webkit-appearance: none; }
    .sb-textarea { resize: vertical; }

    .sb-submit {
      transition: transform 0.2s, box-shadow 0.2s;
      font-family: 'Inter', sans-serif;
    }
    .sb-submit:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 12px 32px rgba(42,191,171,0.45) !important;
    }
    .sb-submit:active:not(:disabled) { transform: translateY(0); }

    @keyframes sb-spin {
      from { transform: rotate(0deg); }
      to   { transform: rotate(360deg); }
    }
    .sb-page-spinner {
      width: 40px; height: 40px;
      border: 3px solid rgba(42,191,171,0.2);
      border-top-color: #2ABFAB;
      border-radius: 50%;
      animation: sb-spin 0.8s linear infinite;
    }
    .sb-btn-spinner {
      display: inline-block;
      width: 20px; height: 20px;
      border: 2.5px solid rgba(255,255,255,0.4);
      border-top-color: #ffffff;
      border-radius: 50%;
      animation: sb-spin 0.8s linear infinite;
    }

    .sb-filter {
      font-family: 'Inter', sans-serif;
      padding: 6px 14px; border-radius: 20px;
      border: 1.5px solid #2ABFAB;
      background: #ffffff; color: #2ABFAB;
      font-weight: 600; font-size: 13px; cursor: pointer;
      transition: background 0.15s, color 0.15s;
    }
    .sb-filter.active, .sb-filter:hover { background: #2ABFAB; color: #ffffff; }

    .sb-date-btn {
      font-family: 'Inter', sans-serif;
      min-width: 56px; padding: 8px 6px; border-radius: 10px;
      border: 1.5px solid #E2E4EF; background: #FAFBFF;
      cursor: pointer; display: flex; flex-direction: column;
      align-items: center; gap: 2px;
      transition: border-color 0.15s, background 0.15s;
    }
    .sb-date-btn:hover  { border-color: #2ABFAB; }
    .sb-date-btn.active { border-color: #2ABFAB; background: #E8FAF8; }

    .sb-time-btn {
      font-family: 'Inter', sans-serif;
      padding: 8px 16px; border-radius: 8px;
      border: 1.5px solid #E2E4EF; background: #FAFBFF;
      color: #2A2D45; font-weight: 600; font-size: 13px; cursor: pointer;
      transition: border-color 0.15s, background 0.15s, color 0.15s;
    }
    .sb-time-btn:hover  { border-color: #2ABFAB; }
    .sb-time-btn.active { border-color: #2ABFAB; background: #2ABFAB; color: #ffffff; }

    .sb-content-row {
      display: flex;
      flex-direction: column;
      gap: 16px;
      width: 100%;
    }
    @media (min-width: 768px) {
      .sb-content-row {
        flex-direction: row;
        align-items: flex-start;
        gap: 24px;
      }
      .sb-left-col {
        flex: 0 0 480px;
        max-width: 480px;
      }
    }
  `}</style>
);

// ── Component ─────────────────────────────────────────────────────────────────
export default function SoloBookingPage() {
  const { slug } = useParams();
  const [healer,            setHealer]            = useState(null);
  const [status,            setStatus]            = useState('loading');
  const [form,              setForm]              = useState({
    name: '', mobile: '', email: '', condition: '', preferredDate: '',
    dateOfBirth: '', gender: '', conditionDuration: '', previousTreatments: '',
  });
  const [countryCode,       setCountryCode]       = useState('+91');
  const [submitting,        setSubmitting]        = useState(false);
  const [errorMsg,          setErrorMsg]          = useState('');
  const [consentChecked,    setConsentChecked]    = useState(false);
  const [selectedDate,      setSelectedDate]      = useState(null);
  const [selectedSlot,      setSelectedSlot]      = useState(null);
  const [sessionTypeFilter, setSessionTypeFilter] = useState('all');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const r = await api.get(`/book/solo/${slug}`);
        if (active) { setHealer(r.data.data); setStatus('ready'); }
      } catch (err) {
        if (!active) return;
        if (err.response?.status === 403 && err.response?.data?.code === 'BOOKING_PENDING') setStatus('pending');
        else if (err.response?.status === 403) setStatus('disabled');
        else setStatus('not_found');
      }
    })();
    return () => { active = false; };
  }, [slug]);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    setErrorMsg('');
    if (!form.name.trim())   { setErrorMsg('Name is required.');   return; }
    if (!form.mobile.trim()) { setErrorMsg('Mobile is required.'); return; }
    if (!form.email.trim()) {
      setErrorMsg('Please enter your email address to receive your login credentials.');
      return;
    }
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(form.email.trim())) {
      setErrorMsg('Please enter a valid email address.');
      return;
    }
    if (healer?.availableSlots?.length > 0 && !selectedSlot) {
      setErrorMsg('Please select a date and time slot.');
      return;
    }
    const e164Mobile = `${countryCode}${form.mobile.replace(/\D/g, '')}`;
    setSubmitting(true);
    try {
      await api.post(`/book/solo/${slug}`, {
        name:          form.name.trim(),
        mobile:        e164Mobile,
        email:         form.email.trim(),
        condition:     form.condition.trim(),
        preferredDate: form.preferredDate || undefined,
        consentGiven:  consentChecked,
        ...(healer.intakeFormEnabled && {
          dateOfBirth:        form.dateOfBirth               || undefined,
          gender:             form.gender                    || undefined,
          conditionDuration:  form.conditionDuration.trim()  || undefined,
          previousTreatments: form.previousTreatments.trim() || undefined,
        }),
      });
      setStatus('submitted');
    } catch (err) {
      setErrorMsg(err.response?.data?.message || 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  // ── Loading ─────────────────────────────────────────────────────────────────
  if (status === 'loading') {
    return (
      <>
        {PAGE_STYLES}
        <div style={s.fullPage}>
          <div className="sb-page-spinner" />
        </div>
      </>
    );
  }

  // ── Not found ───────────────────────────────────────────────────────────────
  if (status === 'not_found') {
    return (
      <>
        {PAGE_STYLES}
        <div style={s.fullPage}>
          <div style={s.statusCard}>
            <div style={{ ...s.statusIcon, background: '#FFF2F2' }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#E05252"
                strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
            <h2 style={s.statusTitle}>Profile Not Found</h2>
            <p style={s.statusBody}>This healer profile does not exist or has been removed.</p>
          </div>
        </div>
      </>
    );
  }

  // ── Disabled ────────────────────────────────────────────────────────────────
  if (status === 'disabled') {
    return (
      <>
        {PAGE_STYLES}
        <div style={s.fullPage}>
          <div style={s.statusCard}>
            <div style={{ ...s.statusIcon, background: '#FFF8ED' }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#E07B22"
                strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
            <h2 style={s.statusTitle}>Bookings Paused</h2>
            <p style={s.statusBody}>This healer is not currently accepting new bookings.</p>
          </div>
        </div>
      </>
    );
  }

  // ── Pending approval ────────────────────────────────────────────────────────
  if (status === 'pending') {
    return (
      <>
        {PAGE_STYLES}
        <div style={s.fullPage}>
          <div style={s.statusCard}>
            <div style={{ ...s.statusIcon, background: '#F0F4FF' }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#5B6AB0"
                strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
            </div>
            <h2 style={s.statusTitle}>Profile Coming Soon</h2>
            <p style={s.statusBody}>This healer profile is awaiting approval. Please check back later.</p>
          </div>
        </div>
      </>
    );
  }

  // ── Slot picker data ────────────────────────────────────────────────────────
  const slotsByDate = (healer?.availableSlots || []).reduce((acc, slot) => {
    if (!acc[slot.date]) acc[slot.date] = [];
    acc[slot.date].push(slot);
    return acc;
  }, {});
  const availableDates = Object.keys(slotsByDate).sort();

  const initials = (healer?.name || 'H')
    .split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

  // ── Main render ─────────────────────────────────────────────────────────────
  return (
    <>
      {PAGE_STYLES}
      <div style={s.page}>

        {/* Dot grid texture overlay */}
        <div style={s.dotGrid} />

        {/* Section A — Header bar */}
        <header style={s.header}>
          <div style={s.headerInner}>
            <span style={s.logoText}>
              LifeChi<span style={{ color: '#2ABFAB' }}>AI</span>
            </span>
            <span style={s.poweredBy}>Powered by LifeChiAI</span>
          </div>
        </header>

        {/* Content column */}
        <div style={s.content}>

          <div style={s.logoBlock}>
            <img src="/logo-web.png" alt="LifeChiAI" style={s.logoImg} />
          </div>

          <div className="sb-content-row">
            <div className="sb-left-col" style={s.leftCol}>

          {/* Section B — Healer identity card */}
          <div style={s.identityCard}>

            {/* B1 — Avatar */}
            {healer?.photoUrl ? (
              <img src={healer.photoUrl} alt={healer.name} style={s.avatarImg} />
            ) : (
              <div style={s.avatar}>{initials}</div>
            )}

            {/* B2 — Name */}
            <h1 style={s.healerName}>{healer?.name}</h1>

            {/* B3 — Specialisation badge */}
            {healer?.domain && (
              <div style={s.domainBadge}>
                <IcLeaf />
                <span>{DOMAIN_LABELS[healer.domain] || healer.domain}</span>
              </div>
            )}

            {/* B4 — City */}
            {healer?.city && (
              <div style={s.cityRow}>
                <IcPin />
                <span style={s.cityText}>{healer.city}</span>
              </div>
            )}

            {/* B5 — Bio */}
            {healer?.bio && (
              <p style={s.bio}>{healer.bio}</p>
            )}

            {/* B6 — Trust indicators */}
            <div style={s.trustRow}>
              <span style={s.trustPill}><IcLock /><span>DPDPA Compliant</span></span>
              <span style={s.trustPill}><IcCheck /><span>Certified Healer</span></span>
              <span style={s.trustPill}><IcShield /><span>Secure Booking</span></span>
            </div>
          </div>

          {/* Fee disclosure — transparency for patients + Razorpay business verification */}
          <p style={{ fontSize: 13, color: '#555870', textAlign: 'left', margin: 0, fontFamily: FONT, lineHeight: 1.5 }}>
            Session fee will be confirmed by {healer?.name}, with a secure payment link shared after your appointment is accepted.
          </p>

          {/* Slot picker — shown only when healer has available slots */}
          {healer?.availableSlots?.length > 0 && (
            <div style={s.sectionCard}>
              <h3 style={s.slotTitle}>Choose a Date &amp; Time</h3>

              {healer.sessionTypes?.length > 1 && (
                <div style={s.filterRow}>
                  {['all', ...(healer.sessionTypes || [])].map(t => (
                    <button
                      key={t}
                      className={`sb-filter${sessionTypeFilter === t ? ' active' : ''}`}
                      onClick={() => setSessionTypeFilter(t)}
                    >
                      {t === 'all' ? 'All' : t === 'in-person' ? 'In-person' : 'Distance'}
                    </button>
                  ))}
                </div>
              )}

              <div style={s.dateRow}>
                {availableDates.map(date => {
                  const d   = new Date(date + 'T00:00:00');
                  const day = d.toLocaleDateString('en-IN', { weekday: 'short' });
                  const dd  = d.getDate();
                  const mon = d.toLocaleDateString('en-IN', { month: 'short' });
                  return (
                    <button
                      key={date}
                      className={`sb-date-btn${selectedDate === date ? ' active' : ''}`}
                      onClick={() => { setSelectedDate(date); setSelectedSlot(null); }}
                    >
                      <span style={s.dateBtnDay}>{day}</span>
                      <span style={s.dateBtnNum}>{dd}</span>
                      <span style={s.dateBtnMon}>{mon}</span>
                    </button>
                  );
                })}
              </div>

              {selectedDate && (
                <div style={s.timeRow}>
                  {(slotsByDate[selectedDate] || [])
                    .filter(sl => sessionTypeFilter === 'all' || sl.sessionType === sessionTypeFilter)
                    .map((slot, i) => (
                      <button
                        key={i}
                        className={`sb-time-btn${selectedSlot === slot ? ' active' : ''}`}
                        onClick={() => {
                          setSelectedSlot(slot);
                          setForm(f => ({ ...f, preferredDate: slot.date + 'T' + slot.startTime }));
                        }}
                      >
                        {slot.startTime}
                      </button>
                    ))
                  }
                </div>
              )}

              {selectedSlot && (
                <p style={s.slotConfirm}>
                  Selected:{' '}
                  {new Date(selectedSlot.date + 'T00:00:00')
                    .toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}
                  {' '}at {selectedSlot.startTime}
                </p>
              )}
            </div>
          )}

          {/* Section C/D — Booking form card (or success state) */}
          <div style={{ ...s.sectionCard, textAlign: 'left', overflow: 'hidden', padding: 0 }}>

            {status === 'submitted' ? (
              /* Section D — Success state */
              <div style={s.successWrap}>
                <div style={s.successCircle}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none"
                    stroke="#ffffff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </div>
                <h2 style={s.successTitle}>Appointment Requested!</h2>
                <p style={s.successBody}>
                  Thank you, {form.name || 'there'}.{' '}
                  {healer?.name || 'Your healer'} will confirm your appointment via WhatsApp shortly.
                </p>
                <p style={s.successNote}>Check your WhatsApp for confirmation</p>
              </div>
            ) : (
              <div style={{ padding: '28px 24px' }}>
                {/* C1 — Form header */}
                <h2 style={s.formTitle}>Book a Session</h2>
                <p style={s.formSubtitle}>Fill in your details and we'll confirm your appointment</p>

                <form onSubmit={handleSubmit} noValidate
                  style={{ display: 'flex', flexDirection: 'column', gap: 18, marginTop: 24 }}>

                  {errorMsg && (
                    <div style={s.errorBanner}>{errorMsg}</div>
                  )}

                  {/* Name */}
                  <div>
                    <label style={s.label}>
                      Your Name <span style={s.required}>*</span>
                    </label>
                    <input
                      name="name" value={form.name} onChange={handleChange}
                      className="sb-input" style={s.input}
                      placeholder="Full name" autoComplete="name"
                    />
                  </div>

                  {/* Country */}
                  <div>
                    <label style={s.label}>Country</label>
                    <div style={{ position: 'relative' }}>
                      <select
                        value={countryCode}
                        onChange={e => setCountryCode(e.target.value)}
                        className="sb-input sb-select"
                        style={{ ...s.input, paddingRight: 36, cursor: 'pointer' }}
                      >
                        {GROUPS.map(group => (
                          <optgroup key={group} label={group}>
                            {COUNTRY_CODES.filter(c => c.group === group).map(c => (
                              <option key={c.value} value={c.value}>{c.label}</option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                      <span style={s.chevron}><IcChevron /></span>
                    </div>
                  </div>

                  {/* Mobile */}
                  <div>
                    <label style={s.label}>
                      Mobile Number <span style={s.required}>*</span>
                    </label>
                    <input
                      name="mobile" value={form.mobile} onChange={handleChange}
                      className="sb-input" style={s.input}
                      placeholder="10-digit number" type="tel" autoComplete="tel"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label style={s.label}>Email address <span style={s.required}>*</span></label>
                    <input
                      name="email" value={form.email} onChange={handleChange}
                      className="sb-input" style={s.input}
                      type="email" placeholder="your@email.com" autoComplete="email"
                    />
                    <span style={s.hint}>You'll receive your login credentials at this address</span>
                  </div>

                  {/* What brings you here */}
                  <div>
                    <label style={s.label}>What brings you here? (optional)</label>
                    <textarea
                      name="condition" value={form.condition} onChange={handleChange}
                      className="sb-input sb-textarea"
                      style={{ ...s.input, height: 'auto', minHeight: 90, padding: '12px 14px' }}
                      placeholder="Briefly describe your concern" rows={3}
                    />
                    <span style={s.hint}>Briefly describe what you'd like healed</span>
                  </div>

                  {/* Intake form extra fields */}
                  {healer.intakeFormEnabled && (
                    <>
                      <div style={s.intakeDivider}>A few more details (optional)</div>

                      <div>
                        <label style={s.label}>Date of birth</label>
                        <input
                          name="dateOfBirth" value={form.dateOfBirth} onChange={handleChange}
                          className="sb-input" style={s.input} type="date"
                        />
                      </div>

                      <div>
                        <label style={s.label}>Gender</label>
                        <div style={{ position: 'relative' }}>
                          <select
                            name="gender" value={form.gender} onChange={handleChange}
                            className="sb-input sb-select"
                            style={{ ...s.input, paddingRight: 36, cursor: 'pointer' }}
                          >
                            <option value="">Select…</option>
                            <option value="male">Male</option>
                            <option value="female">Female</option>
                            <option value="other">Other</option>
                            <option value="prefer_not_to_say">Prefer not to say</option>
                          </select>
                          <span style={s.chevron}><IcChevron /></span>
                        </div>
                      </div>

                      <div>
                        <label style={s.label}>How long have you had this condition?</label>
                        <input
                          name="conditionDuration" value={form.conditionDuration} onChange={handleChange}
                          className="sb-input" style={s.input}
                          placeholder="e.g. 2 weeks, 3 months…"
                        />
                      </div>

                      <div>
                        <label style={s.label}>Previous treatments tried</label>
                        <textarea
                          name="previousTreatments" value={form.previousTreatments} onChange={handleChange}
                          className="sb-input sb-textarea"
                          style={{ ...s.input, height: 'auto', minHeight: 72, padding: '12px 14px' }}
                          placeholder="e.g. physiotherapy, medication, yoga…" rows={2}
                        />
                      </div>
                    </>
                  )}

                  {/* Preferred date */}
                  <div>
                    <label style={s.label}>Preferred date (optional)</label>
                    <input
                      name="preferredDate" value={form.preferredDate} onChange={handleChange}
                      className="sb-input" style={s.input} type="date"
                    />
                  </div>

                  {/* C3 — Consent */}
                  <div style={s.consentRow}>
                    <div
                      style={{
                        ...s.checkbox,
                        ...(consentChecked ? s.checkboxOn : {}),
                      }}
                      role="checkbox"
                      aria-checked={consentChecked}
                      tabIndex={0}
                      onClick={() => setConsentChecked(c => !c)}
                      onKeyDown={e => {
                        if (e.key === ' ' || e.key === 'Enter') {
                          e.preventDefault();
                          setConsentChecked(c => !c);
                        }
                      }}
                    >
                      {consentChecked && (
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                          stroke="#ffffff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      )}
                    </div>
                    <span style={s.consentText}>
                      I agree that {healer?.name} may contact me about this appointment.{' '}
                      <a href="https://www.lifechiai.com/privacy" target="_blank" rel="noopener noreferrer" style={s.privacyLink}>
                        Privacy Policy
                      </a>
                    </span>
                  </div>

                  {/* C4 — Submit */}
                  <button
                    type="submit"
                    className="sb-submit"
                    style={{
                      ...s.submitBtn,
                      opacity: (!consentChecked || submitting) ? 0.75 : 1,
                      cursor:  (!consentChecked || submitting) ? 'not-allowed' : 'pointer',
                    }}
                    disabled={!consentChecked || submitting}
                  >
                    {submitting
                      ? <span className="sb-btn-spinner" />
                      : 'Request Appointment'
                    }
                  </button>

                  {/* C5 — Below-button note */}
                  <div style={s.privacyNote}>
                    <IcLock size={12} color="#9094AE" />
                    <span>Your information is protected under DPDPA 2023</span>
                  </div>

                </form>
              </div>
            )}
          </div>

            </div>{/* end sb-left-col */}

            <div style={s.rightCol}>
              <div style={s.infoCard}>
                <h2 style={s.infoHeading}>What is Pranic Healing?</h2>

                <p style={s.infoPara}>
                  Pranic Healing is a no-touch, no-drug energy healing system developed by Grand Master Choa Kok Sui. It works by cleansing depleted or congested energy from the body's energy field (aura and chakras) and replenishing it with fresh, vital prana — the life force that sustains the body.
                </p>

                <div>
                  <p style={s.infoSubheading}>What can it help with?</p>
                  <ul style={s.infoList}>
                    <li style={s.infoListItem}>Physical conditions: pain, fatigue, respiratory issues, skin disorders</li>
                    <li style={s.infoListItem}>Emotional concerns: stress, anxiety, grief, relationship difficulties</li>
                    <li style={s.infoListItem}>Mental clarity: focus, sleep quality, mental exhaustion</li>
                    <li style={s.infoListItem}>Spiritual growth: meditation deepening, energy sensitivity</li>
                  </ul>
                </div>

                <div>
                  <p style={s.infoSubheading}>How does a session work?</p>
                  <p style={s.infoPara}>
                    Sessions are conducted either in-person or at a distance — both are equally effective. Your healer scans your energy field, removes stress and congestion, then energises specific areas. Most clients feel calmer, lighter, or more grounded within hours. Each session typically lasts 30–60 minutes.
                  </p>
                </div>

                <div style={s.infoQuote}>
                  <p style={s.infoQuoteText}>
                    "I've had four sessions and my chronic back pain has reduced significantly. I was sceptical at first, but the results speak for themselves."
                  </p>
                  <p style={s.infoQuoteAttrib}>— Patient, LifeChiAI Platform</p>
                </div>

                <div style={s.infoQuote}>
                  <p style={s.infoQuoteText}>
                    "After two sessions for anxiety, I sleep much better and feel more centered. It's become part of my monthly wellness routine."
                  </p>
                  <p style={s.infoQuoteAttrib}>— Patient, LifeChiAI Platform</p>
                </div>

                {healer?.phone && (
                  <a
                    href={`https://wa.me/${healer.phone.replace(/[^\d]/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={s.infoCta}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="#25D366">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    <span style={s.infoCtaText}>Chat on WhatsApp before booking</span>
                  </a>
                )}

                <p style={s.infoDisclaimer}>
                  Pranic Healing is a complementary modality and is not a substitute for professional medical advice, diagnosis, or treatment. Always consult a qualified healthcare professional for medical conditions.
                </p>
              </div>
            </div>{/* end sb-right-col */}

          </div>{/* end sb-content-row */}

          {/* Section E — Footer */}
          <footer style={s.footer}>
            <p style={s.footerLine}>Powered by LifeChiAI · Enerqubix AI Private Limited</p>
            <p style={s.footerLine}>
              © 2026 ·{' '}
              <a href="https://www.lifechiai.com/privacy" target="_blank" rel="noopener noreferrer" style={s.footerAnchor}>
                Privacy Policy
              </a>
              {' '}· DPDPA 2023 Compliant
            </p>
          </footer>

        </div>
      </div>
    </>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

const s = {
  // Page shell
  page: {
    position: 'relative',
    minHeight: '100vh',
    background: 'linear-gradient(160deg, #F0F4FF 0%, #F7F8FC 50%, #EEF9F7 100%)',
    fontFamily: FONT,
  },
  dotGrid: {
    position: 'fixed',
    inset: 0,
    backgroundImage: 'radial-gradient(circle, #C8CBE0 1px, transparent 1px)',
    backgroundSize: '28px 28px',
    opacity: 0.35,
    pointerEvents: 'none',
    zIndex: 0,
  },
  fullPage: {
    position: 'relative',
    minHeight: '100vh',
    background: 'linear-gradient(160deg, #F0F4FF 0%, #F7F8FC 50%, #EEF9F7 100%)',
    fontFamily: FONT,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },

  // Header
  header: {
    position: 'sticky',
    top: 0,
    zIndex: 100,
    height: 52,
    backgroundColor: 'rgba(247,248,252,0.95)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    borderBottom: '1px solid #E2E4EF',
    boxShadow: '0 1px 4px rgba(13,15,31,0.06)',
    display: 'flex',
    alignItems: 'center',
  },
  headerInner: {
    width: '100%',
    maxWidth: 480,
    margin: '0 auto',
    padding: '0 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  logoText: {
    fontFamily: FONT,
    fontWeight: 700,
    fontSize: 16,
    color: '#0D0F1F',
  },
  poweredBy: {
    fontSize: 11,
    color: '#9094AE',
    fontFamily: FONT,
  },

  // Content column
  content: {
    position: 'relative',
    zIndex: 1,
    maxWidth: 960,
    margin: '0 auto',
    padding: '20px 16px 32px',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },

  // Base card
  sectionCard: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #E2E4EF',
    borderRadius: 20,
    padding: '28px 24px',
    boxShadow: '0 4px 20px rgba(13,15,31,0.08)',
  },

  // Healer identity card
  identityCard: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #E2E4EF',
    borderRadius: 20,
    padding: '32px 24px',
    boxShadow: '0 4px 20px rgba(13,15,31,0.08)',
  },

  // Avatar
  avatar: {
    width: 72,
    height: 72,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #2ABFAB, #37AFF4)',
    color: '#FFFFFF',
    fontSize: 26,
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 16px rgba(42,191,171,0.3)',
    fontFamily: FONT,
  },
  avatarImg: {
    width: 72,
    height: 72,
    borderRadius: '50%',
    border: '3px solid #2ABFAB',
    objectFit: 'cover',
    display: 'block',
  },

  // Healer identity text
  healerName: {
    fontFamily: FONT,
    fontSize: 22,
    fontWeight: 700,
    color: '#0D0F1F',
    margin: '14px 0 0',
  },
  domainBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    background: '#E8FAF8',
    border: '1px solid rgba(42,191,171,0.3)',
    borderRadius: 20,
    padding: '5px 14px',
    fontSize: 13,
    fontWeight: 600,
    color: '#1A9E8C',
    marginTop: 8,
    fontFamily: FONT,
  },
  cityRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 4,
    marginTop: 6,
  },
  cityText: {
    fontSize: 14,
    color: '#555870',
    fontFamily: FONT,
  },
  bio: {
    fontSize: 14,
    lineHeight: 1.65,
    color: '#2A2D45',
    marginTop: 14,
    fontFamily: FONT,
  },
  trustRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 16,
  },
  trustPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    background: '#F7F8FC',
    border: '1px solid #E2E4EF',
    borderRadius: 20,
    padding: '4px 10px',
    fontSize: 11,
    color: '#555870',
    fontFamily: FONT,
  },

  // Slot picker
  slotTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: '#0D0F1F',
    margin: '0 0 12px',
    fontFamily: FONT,
  },
  filterRow: {
    display: 'flex',
    gap: 8,
    marginBottom: 12,
    flexWrap: 'wrap',
  },
  dateRow: {
    display: 'flex',
    gap: 8,
    overflowX: 'auto',
    paddingBottom: 8,
    marginBottom: 12,
  },
  dateBtnDay: { fontSize: 10, color: '#9094AE', fontWeight: 600, textTransform: 'uppercase', fontFamily: FONT },
  dateBtnNum: { fontSize: 18, fontWeight: 800, color: '#0D0F1F', fontFamily: FONT },
  dateBtnMon: { fontSize: 10, color: '#9094AE', fontFamily: FONT },
  timeRow:    { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 },
  slotConfirm:{ fontSize: 13, color: '#1A9E8C', fontWeight: 600, margin: 0, fontFamily: FONT },

  // Form
  formTitle: {
    fontFamily: FONT,
    fontSize: 18,
    fontWeight: 700,
    color: '#0D0F1F',
    margin: 0,
  },
  formSubtitle: {
    fontSize: 13,
    color: '#555870',
    margin: '4px 0 0',
    fontFamily: FONT,
  },
  label: {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: '#2A2D45',
    marginBottom: 6,
    fontFamily: FONT,
  },
  required: { color: '#E05252' },
  input: {
    display: 'block',
    width: '100%',
    height: 46,
    padding: '0 14px',
    border: '1.5px solid #E2E4EF',
    borderRadius: 10,
    fontSize: 14,
    color: '#0D0F1F',
    background: '#FAFBFF',
    outline: 'none',
    fontFamily: FONT,
    boxSizing: 'border-box',
  },
  chevron: {
    position: 'absolute',
    right: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
  },
  hint: {
    display: 'block',
    fontSize: 11,
    color: '#9094AE',
    marginTop: 4,
    fontFamily: FONT,
  },
  intakeDivider: {
    fontSize: 13,
    fontWeight: 700,
    color: '#1A9E8C',
    borderTop: '1px solid #E2E4EF',
    paddingTop: 16,
    fontFamily: FONT,
  },

  // Consent
  consentRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
  },
  checkbox: {
    width: 18,
    height: 18,
    flexShrink: 0,
    marginTop: 2,
    border: '1.5px solid #CDD0E3',
    borderRadius: 5,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s',
    userSelect: 'none',
    background: '#ffffff',
  },
  checkboxOn: {
    background: '#2ABFAB',
    borderColor: '#2ABFAB',
  },
  consentText: {
    fontSize: 13,
    color: '#555870',
    lineHeight: 1.5,
    fontFamily: FONT,
  },
  privacyLink: {
    color: '#2ABFAB',
    textDecoration: 'underline',
  },

  // Submit button
  submitBtn: {
    width: '100%',
    height: 52,
    background: 'linear-gradient(135deg, #2ABFAB, #37AFF4)',
    border: 'none',
    borderRadius: 12,
    fontSize: 16,
    fontWeight: 700,
    color: '#FFFFFF',
    boxShadow: '0 8px 24px rgba(42,191,171,0.35)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: FONT,
  },

  // Privacy note below button
  privacyNote: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 5,
    fontSize: 12,
    color: '#9094AE',
    fontFamily: FONT,
    marginTop: -4,
  },

  // Error banner
  errorBanner: {
    backgroundColor: '#FFF2F2',
    border: '1px solid #FCA5A5',
    color: '#DC2626',
    borderRadius: 8,
    padding: '10px 14px',
    fontSize: 14,
    fontFamily: FONT,
  },

  // Status cards (not_found / disabled)
  statusCard: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #E2E4EF',
    borderRadius: 20,
    padding: '40px 32px',
    textAlign: 'center',
    maxWidth: 360,
    boxShadow: '0 4px 20px rgba(13,15,31,0.08)',
  },
  statusIcon: {
    width: 64,
    height: 64,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px',
  },
  statusTitle: {
    fontSize: 20,
    fontWeight: 700,
    color: '#0D0F1F',
    margin: '0 0 8px',
    fontFamily: FONT,
  },
  statusBody: {
    fontSize: 14,
    color: '#555870',
    lineHeight: 1.6,
    fontFamily: FONT,
    margin: 0,
  },

  // Success state (Section D)
  successWrap: {
    background: 'linear-gradient(135deg, #E8FAF8, #F0ECFD)',
    border: '1px solid rgba(42,191,171,0.2)',
    borderRadius: 20,
    padding: '40px 24px',
    textAlign: 'center',
  },
  successCircle: {
    width: 72,
    height: 72,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #2ABFAB, #37AFF4)',
    boxShadow: '0 8px 24px rgba(42,191,171,0.35)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto',
  },
  successTitle: {
    fontFamily: FONT,
    fontSize: 22,
    fontWeight: 700,
    color: '#0D0F1F',
    marginTop: 20,
    marginBottom: 0,
  },
  successBody: {
    fontSize: 15,
    color: '#2A2D45',
    lineHeight: 1.65,
    marginTop: 12,
    fontFamily: FONT,
  },
  successNote: {
    fontSize: 13,
    color: '#555870',
    marginTop: 8,
    fontFamily: FONT,
  },

  // Logo block (Diff C)
  logoBlock: {
    display: 'flex',
    justifyContent: 'flex-start',
    paddingLeft: 4,
    marginBottom: 8,
  },
  logoImg: {
    height: 44,
    width: 'auto',
  },

  // Two-column layout (Diff D)
  leftCol: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    minWidth: 0,
  },
  rightCol: {
    flex: 1,
    minWidth: 0,
  },
  infoCard: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #E2E4EF',
    borderRadius: 20,
    padding: '28px 24px',
    boxShadow: '0 4px 20px rgba(13,15,31,0.08)',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  infoHeading: {
    fontFamily: FONT,
    fontSize: 20,
    fontWeight: 700,
    color: '#0D0F1F',
    margin: 0,
  },
  infoPara: {
    fontSize: 14,
    lineHeight: 1.7,
    color: '#555870',
    margin: 0,
    fontFamily: FONT,
  },
  infoSubheading: {
    fontFamily: FONT,
    fontSize: 15,
    fontWeight: 700,
    color: '#0D0F1F',
    margin: '4px 0 0',
  },
  infoList: {
    margin: '4px 0 0',
    paddingLeft: 20,
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  infoListItem: {
    fontSize: 14,
    color: '#555870',
    lineHeight: 1.6,
    fontFamily: FONT,
  },
  infoQuote: {
    background: '#F0F4FF',
    border: '1px solid #E2E4EF',
    borderLeft: '3px solid #2ABFAB',
    borderRadius: '0 8px 8px 0',
    padding: '12px 16px',
  },
  infoQuoteText: {
    fontSize: 13,
    color: '#2A2D45',
    lineHeight: 1.65,
    fontStyle: 'italic',
    margin: 0,
    fontFamily: FONT,
  },
  infoQuoteAttrib: {
    fontSize: 12,
    color: '#9094AE',
    marginTop: 6,
    fontFamily: FONT,
  },
  infoDisclaimer: {
    fontSize: 12,
    color: '#9094AE',
    lineHeight: 1.5,
    margin: 0,
    fontFamily: FONT,
  },
  infoCta: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    backgroundColor: '#E8FAF8',
    border: '1px solid rgba(42,191,171,0.3)',
    borderRadius: 12,
    padding: '14px 16px',
    textDecoration: 'none',
  },
  infoCtaText: {
    fontSize: 14,
    fontWeight: 600,
    color: '#1A9E8C',
    fontFamily: FONT,
  },

  // Footer
  footer: {
    marginTop: 8,
    paddingBottom: 8,
    textAlign: 'center',
  },
  footerLine: {
    fontSize: 12,
    color: '#9094AE',
    margin: '4px 0',
    fontFamily: FONT,
  },
  footerAnchor: {
    color: '#9094AE',
    textDecoration: 'underline',
  },
};
